import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Company } from '../types/models';
import { companiesAPI } from "../utils/api";
import { logger } from '../utils/logger';

// ARCHITECTURE IMPORTANTE - Contact Info Company:
// ===============================================
// Le modèle Company n'a PLUS de champs email/phone.
//
// Les contacts Company sont désormais gérés ainsi:
// - Email Company: Utiliser User.email (propriétaire/owner de la Company)
// - Phone Company: N'existe PLUS au niveau Company
// - Contact Client: Utiliser Client.email et Client.phone
// - Contact Fournisseur: Utiliser Supplier.email et Supplier.phone
//
// Pour afficher le contact d'une Company, faire:
// 1. Récupérer le User propriétaire (role OWNER pour cette Company)
// 2. Afficher User.email comme email de contact de la Company
//
// Voir prisma/schema.prisma lignes 181-231 (Company), 244-271 (User)

interface CompanyState {
  company: Company | null;
  isLoading: boolean;
  error: string | null;
  // Couleurs de template (chargées via useTemplateColors, stockées ici pour compatibilité)
  invoiceColor?: string;
  quoteColor?: string;

  // Actions
  setCompany: (company: Company | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  loadCompany: (companyId: string) => Promise<void>;
  clearCompany: () => void;
}

// API_URL supprimé - maintenant géré par companiesAPI

export const useCompanyStore = create<CompanyState>()(
  devtools(
    persist(
      (set, get) => ({
        company: null,
        isLoading: false,
        error: null,

        setCompany: (company) => {
          set({ company });
        },

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error }),

        loadCompany: async (companyId: string) => {
          const currentState = get();

          // Si on a déjà les données en cache, ne pas afficher le loading
          // mais toujours recharger depuis l'API pour avoir les données fraîches
          if (currentState.company?.id === companyId && !currentState.error) {
            // Refresh silencieux en arrière-plan (pas de loading spinner)
            try {
              const data = await companiesAPI.getById(companyId);
              if (data.logoUrl && !data.logoUrl.startsWith('http')) {
                const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";
                data.logoUrl = `${API_URL}/${data.logoUrl}`;
              }
              set({ company: data, error: null });
            } catch {
              // Silencieux — on garde le cache
            }
            return;
          }

          set({ isLoading: true, error: null });

          try {
            const data = await companiesAPI.getById(companyId);

            // Construire l'URL complète pour le logo (logoUrl en camelCase Prisma V2)
            if (data.logoUrl && !data.logoUrl.startsWith('http')) {
              const API_URL =
                import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";
              data.logoUrl = `${API_URL}/${data.logoUrl}`;
            }

            set({
              company: data,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Erreur inconnue';

            logger.error('Erreur chargement société:', error);

            set({
              company: null,
              isLoading: false,
              error: errorMessage,
            });
          }
        },

        clearCompany: () => {
          set({
            company: null,
            isLoading: false,
            error: null,
          });
        },
      }),
      {
        name: 'company-storage',
        version: 2, // Incrémente pour invalider le cache
        partialize: (state) => ({
          company: state.company,
        }),
        migrate: () => ({
          company: null,
          isLoading: false,
          error: null,
        }),
      }
    ),
    { name: 'CompanyStore' }
  )
);
